import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiError } from '../utils/apiError';
import { format, isToday, isPast, differenceInDays } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import {
    CheckSquare, Phone, Mail, Linkedin, ListTodo, Calendar,
    AlertCircle, Loader2, Filter, Repeat
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import LogInteractionModal from '../components/LogInteractionModal';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TasksPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [tasks, setTasks] = useState([]);
    const [teamMembers, setTeamMembers] = useState([]);
    const [leads, setLeads] = useState({}); // Map of lead_id -> lead data
    const [loading, setLoading] = useState(true);

    // Filters
    const [assigneeFilter, setAssigneeFilter] = useState('me'); // 'me', 'all', or user_id
    const [dateFilter, setDateFilter] = useState('all'); // 'today_overdue', 'upcoming', 'all'

    // Log interaction modal state
    const [logInteractionOpen, setLogInteractionOpen] = useState(false);
    const [completingTask, setCompletingTask] = useState(null);

    useEffect(() => {
        fetchTeamMembers();
        fetchTasks();
    }, [assigneeFilter, dateFilter, user]);

    const fetchTeamMembers = async () => {
        try {
            const response = await axios.get(`${API_URL}/team/members`);
            setTeamMembers(response.data);
        } catch (error) {
            console.error('Failed to fetch team members:', error);
        }
    };

    const fetchTasks = async () => {
        try {
            setLoading(true);

            // Build query params
            const params = {};
            if (assigneeFilter === 'me' && user?.id) {
                params.assignee_id = user.id;
            } else if (assigneeFilter !== 'all') {
                params.assignee_id = assigneeFilter;
            }
            params.status = 'Pending';

            const response = await axios.get(`${API_URL}/tasks`, { params });
            let fetchedTasks = response.data;

            // Apply date filter
            const now = new Date();
            now.setHours(0, 0, 0, 0);

            if (dateFilter === 'today_overdue') {
                fetchedTasks = fetchedTasks.filter(task => {
                    const dueDate = new Date(task.due_date);
                    dueDate.setHours(0, 0, 0, 0);
                    return dueDate <= now;
                });
            } else if (dateFilter === 'upcoming') {
                fetchedTasks = fetchedTasks.filter(task => {
                    const dueDate = new Date(task.due_date);
                    dueDate.setHours(0, 0, 0, 0);
                    return dueDate > now;
                });
            }
            // 'all' shows everything, no filter

            setTasks(fetchedTasks);

            // Fetch lead data for all tasks
            const leadIds = [...new Set(fetchedTasks.map(t => t.lead_id))];
            const leadPromises = leadIds.map(leadId =>
                axios.get(`${API_URL}/leads/${leadId}`).catch(() => null)
            );
            const leadResponses = await Promise.all(leadPromises);
            const leadsMap = {};
            leadResponses.forEach(response => {
                if (response?.data) {
                    leadsMap[response.data.id] = response.data;
                }
            });
            setLeads(leadsMap);
        } catch (error) {
            console.error('Failed to fetch tasks:', error);
            toast.error('Failed to load tasks');
        } finally {
            setLoading(false);
        }
    };

    const handleCompleteTask = (task) => {
        if (completingTask) return;
        setCompletingTask(task);
        setLogInteractionOpen(true);
    };

    const handleInteractionSaved = async () => {
        try {
            await axios.patch(`${API_URL}/tasks/${completingTask.id}`, {
                status: 'Completed'
            });
            toast.success('Task marked as complete');
            fetchTasks();
        } catch (error) {
            toast.error(getApiError(error, 'Failed to complete task'));
        } finally {
            setLogInteractionOpen(false);
            setCompletingTask(null);
        }
    };

    const handleLogInteractionClose = () => {
        setLogInteractionOpen(false);
        setCompletingTask(null);
    };

    const getTaskTypeIcon = (type) => {
        if (!type) {
            return <CheckSquare className="h-4 w-4" />;
        }
        switch (type) {
            case 'Call':
                return <Phone className="h-4 w-4" />;
            case 'Email':
                return <Mail className="h-4 w-4" />;
            case 'LinkedIn':
                return <Linkedin className="h-4 w-4" />;
            case 'Todo':
                return <ListTodo className="h-4 w-4" />;
            default:
                return <CheckSquare className="h-4 w-4" />;
        }
    };

    const getInitials = (email) => {
        if (!email) return '?';
        const username = email.split('@')[0];
        if (username.includes('.')) {
            const parts = username.split('.').filter(p => p.length > 0);
            if (parts.length >= 2) {
                return (parts[0][0] + parts[1][0]).toUpperCase();
            }
        }
        return username.length >= 2 ? username.substring(0, 2).toUpperCase() : username.toUpperCase() + '?';
    };

    const categorizeTasks = () => {
        const now = new Date();
        now.setHours(0, 0, 0, 0);

        const overdue = [];
        const today = [];
        const upcoming = [];

        tasks.forEach(task => {
            const dueDate = new Date(task.due_date);
            dueDate.setHours(0, 0, 0, 0);

            if (dueDate < now) {
                overdue.push(task);
            } else if (isToday(dueDate)) {
                today.push(task);
            } else {
                upcoming.push(task);
            }
        });

        return { overdue, today, upcoming };
    };

    const renderTaskCard = (task) => {
        if (!task || !task.id) {
            return null;
        }
        const lead = leads[task.lead_id];
        const assignee = teamMembers.find(m => m.id === task.assignee_id);
        const dueDate = task.due_date ? new Date(task.due_date) : new Date();
        const isOverdue = isPast(dueDate) && !isToday(dueDate);
        const daysOverdue = isOverdue ? differenceInDays(new Date(), dueDate) : 0;

        return (
            <div
                className={`p-4 rounded-lg border transition-all hover:shadow-md ${isOverdue
                    ? 'bg-red-50 border-red-200'
                    : isToday(dueDate)
                        ? 'bg-green-50 border-green-200'
                        : 'bg-white border-slate-200'
                    }`}
            >
                <div className="flex items-start gap-3">
                    <div className={`mt-1 ${isOverdue ? 'text-red-600' : isToday(dueDate) ? 'text-green-600' : 'text-slate-600'}`}>
                        {getTaskTypeIcon(task.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 mb-2">
                            {task.description}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-slate-600 mb-2 flex-wrap">
                            {lead && (
                                <button
                                    onClick={() => navigate(`/leads/${task.lead_id}`)}
                                    className="flex items-center gap-1 hover:text-slate-900 transition-colors"
                                >
                                    <span className="font-medium">{lead.name}</span>
                                    {lead.company && (
                                        <span className="text-slate-400">• {lead.company}</span>
                                    )}
                                </button>
                            )}
                            <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {dueDate && !isNaN(dueDate.getTime()) ? format(dueDate, 'MMM d, yyyy') : 'Invalid date'}
                            </span>
                            {isOverdue && (
                                <span className="text-red-600 font-medium flex items-center gap-1">
                                    <AlertCircle className="h-3 w-3" />
                                    {daysOverdue} {daysOverdue === 1 ? 'Day' : 'Days'} Overdue
                                </span>
                            )}
                            {task.cadence_name && (
                                <span className="flex items-center gap-1 bg-violet-50 text-violet-600 border border-violet-200 px-2 py-0.5 rounded-full font-medium">
                                    <Repeat className="h-3 w-3" />
                                    {task.cadence_name}
                                </span>
                            )}
                        </div>
                        {assigneeFilter === 'all' && assignee && (
                            <div className="flex items-center gap-2 mt-2">
                                <Avatar className="h-5 w-5">
                                    <AvatarFallback className="text-xs bg-slate-200">
                                        {getInitials(assignee.email)}
                                    </AvatarFallback>
                                </Avatar>
                                <span className="text-xs text-slate-500">{assignee.email}</span>
                            </div>
                        )}
                    </div>
                    <div className="flex-shrink-0">
                        <Checkbox
                            checked={false}
                            disabled={!!completingTask}
                            onCheckedChange={() => handleCompleteTask(task)}
                            className="rounded"
                        />
                    </div>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="h-[calc(100vh-4rem)] flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
        );
    }

    const { overdue, today, upcoming } = categorizeTasks();

    return (
        <>
        <div className="h-[calc(100vh-4rem)] bg-slate-50/50 flex flex-col">
            {/* Header */}
            <div className="bg-white border-b border-slate-200 p-6">
                <h1 className="text-3xl font-bold text-slate-900 mb-4">My Day</h1>

                {/* Filter Toolbar */}
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                        <Filter className="h-4 w-4 text-slate-500" />
                        <span className="text-sm font-medium text-slate-700">Filters:</span>
                    </div>

                    <div className="flex items-center gap-2">
                        <Label className="text-sm text-slate-600">Assignee:</Label>
                        <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
                            <SelectTrigger className="w-[180px] rounded-lg">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="me">Me</SelectItem>
                                <SelectItem value="all">All Users</SelectItem>
                                {teamMembers.map((member) => (
                                    <SelectItem key={member.id} value={member.id}>
                                        {member.email}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="flex items-center gap-2">
                        <Label className="text-sm text-slate-600">Date:</Label>
                        <Select value={dateFilter} onValueChange={setDateFilter}>
                            <SelectTrigger className="w-[180px] rounded-lg">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="today_overdue">Today & Overdue</SelectItem>
                                <SelectItem value="upcoming">Upcoming</SelectItem>
                                <SelectItem value="all">All</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
                <div className="max-w-6xl mx-auto space-y-6">
                    {/* Overdue Section */}
                    {overdue.length > 0 && (
                        <div>
                            <div className="flex items-center gap-2 mb-4">
                                <AlertCircle className="h-5 w-5 text-red-600" />
                                <h2 className="text-xl font-semibold text-slate-900">🚨 Overdue</h2>
                                <span className="text-sm text-slate-500">({overdue.length})</span>
                            </div>
                            <div className="space-y-3">
                                {overdue.map(task => (
                                    <div key={task.id}>
                                        {renderTaskCard(task)}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Today Section */}
                    {today.length > 0 && (
                        <div>
                            <div className="flex items-center gap-2 mb-4">
                                <CheckSquare className="h-5 w-5 text-green-600" />
                                <h2 className="text-xl font-semibold text-slate-900">🟢 Today</h2>
                                <span className="text-sm text-slate-500">({today.length})</span>
                            </div>
                            <div className="space-y-3">
                                {today.map(task => (
                                    <div key={task.id}>
                                        {renderTaskCard(task)}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Upcoming Section (only shown if dateFilter is 'upcoming' or 'all') */}
                    {(dateFilter === 'upcoming' || dateFilter === 'all') && upcoming.length > 0 && (
                        <div>
                            <div className="flex items-center gap-2 mb-4">
                                <Calendar className="h-5 w-5 text-slate-600" />
                                <h2 className="text-xl font-semibold text-slate-900">Upcoming</h2>
                                <span className="text-sm text-slate-500">({upcoming.length})</span>
                            </div>
                            <div className="space-y-3">
                                {upcoming.map(task => (
                                    <div key={task.id}>
                                        {renderTaskCard(task)}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Empty State */}
                    {tasks.length === 0 && (
                        <div className="text-center py-12">
                            <CheckSquare className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                            <p className="text-slate-500">No tasks found</p>
                        </div>
                    )}
                </div>
            </div>
        </div>

            {completingTask && (
                <LogInteractionModal
                    open={logInteractionOpen}
                    onClose={handleLogInteractionClose}
                    leadId={completingTask.lead_id}
                    leadName={leads[completingTask.lead_id]?.name}
                    onSaved={handleInteractionSaved}
                />
            )}
        </>
    );
};

export default TasksPage;

